/* eslint-disable max-lines */
import {
  useState,
  type ChangeEventHandler,
  FocusEventHandler,
  FormEventHandler,
  ChangeEvent,
} from 'react';

import { isAddressUpdate, type FetchAddressByPostalCode } from './address';
import { createInvalidErrors } from './createInvalidErrors';
import { familyStructureValidator } from './familyStructure';
import { genderValidator, isGenderId, type GenderId } from './gender';
import {
  defaultHasChildren,
  isHasChildrenId,
  type HasChildrenId,
} from './hasChildren';
import {
  defaultMarriageStatus,
  isMarriageStatusId,
  type MarriageStatusId,
} from './marriageStatus';
import { isPrefectureId, type PrefectureId } from './prefecture';
import {
  acceptedTypesImageExtensions,
  mightExtractImageExtFromValidFileType,
  type AcceptedTypesImageExtension,
  isAcceptableFileSize,
  acceptableSizeThreshold,
} from './profileImage';
import {
  BorrowingPeriodId,
  defaultBorrowingPeriod,
  defaultScholarshipStatus,
  defaultScholarshipType,
  isBorrowingPeriodId,
  isScholarshipStatusId,
  isScholarshipTypeId,
  ScholarshipStatusId,
  ScholarshipTypeId,
} from './scholarship';
import { AcademicBackgroundUpdaters } from './updateAcademicBackground';
import {
  createInitUpdateBasicInfoColumns,
  type BasicInfoUpdaters,
  createProfileUpdaterRequest,
  createUpdateFailedMessage,
} from './updateBasicInfo';
import { QualificationsAndWorkUpdaters } from './updateQualificationsAndWork';

import type { InvalidParam } from '../../../../types/InvalidParam';
import type { LoggedInUser } from '../../../../types/LoggedInUser';
import type { Validation } from '../../../../types/Validation';

import {
  Birthdate,
  BirthMonth,
  createBirthdate,
  createBirthdayList,
  createInitBirthMonthList,
  createInitBirthYearList,
  isSuccessResult,
  prefectureTokyo,
} from '@/packages/nurse-senka-frontend-sdk';

export type ProfileSettingInvalidParam = InvalidParam & {
  name:
    | string
    | 'profileImage'
    | 'profileImageExtension'
    | 'selfIntroduction'
    | 'preferredUsername'
    | 'givenName'
    | 'familyName'
    | 'givenNameKana'
    | 'familyNameKana'
    | 'gender'
    | 'birthdate'
    | 'email'
    | 'phoneNumber'
    | 'postalCode'
    | 'prefectureId'
    | 'locality'
    | 'streetAddress'
    | 'building'
    | 'marriageStatus'
    | 'hasChildren'
    | 'qualificationCodeList'
    | 'workplaceType'
    | 'department'
    | 'activeRegion'
    | 'professionalQualifications'
    | 'expertQualifications'
    | 'otherQualifications'
    | 'otherQualificationsFreeSpace'
    | 'nurseLicenseNumber'
    | 'schoolName'
    | 'graduationYear'
    | 'schoolTypeId'
    | 'borrowingStatus'
    | 'borrowingType'
    | 'borrowingYear';
};

export type ProfileSettingInvalidParams = ProfileSettingInvalidParam[];

export type NameValidator = Validation<{
  name:
    | 'givenName'
    | 'familyName'
    | 'givenNameKana'
    | 'familyNameKana'
    | string;
  reason: string;
}>;

export type PreferredUsernameValidator = Validation<{
  name: 'preferredUsername' | string;
  reason: string;
}>;

export type PhoneNumberValidator = Validation<{
  name: 'phoneNumber' | string;
  reason: string;
}>;

export type PostalCodeValidator = Validation<{
  name: 'postalCode' | string;
  reason: string;
}>;

export type AddressValidator = Validation<{
  name:
    | string
    | 'postalCode'
    | 'prefectureId'
    | 'locality'
    | 'streetAddress'
    | 'building';
  reason: string;
}>;

export type ProfileValidator = Validation<{
  name: 'profileImage' | 'profileImageExtension' | 'selfIntroduction' | string;
  reason: string;
}>;

export type QualificationCodeListValidator = Validation<{
  name: 'qualificationCodeList' | string;
  reason: string;
}>;

export type EmploymentIdValidator = Validation<{
  name: 'employmentId' | string;
  reason: string;
}>;

export type AffiliationIdsValidator = Validation<{
  name: 'affiliationIds' | string;
  reason: string;
}>;

export type ActivityAreaIdsValidator = Validation<{
  name: 'activityAreaIds' | string;
  reason: string;
}>;

export type LicenseIdsValidator = Validation<{
  name: 'licenseIds' | string;
  reason: string;
}>;

export type CustomLicenseValidator = Validation<{
  name: 'customLicense' | string;
  reason: string;
}>;

export type NurseLicenseNumberValidator = Validation<{
  name: 'nurseLicenseNumber' | string;
  reason: string;
}>;

export type SchoolValidator = Validation<{
  name: 'schoolName' | 'schoolTypeId' | 'graduationYear' | string;
  reason: string;
}>;

export type ScholarshipValidator = Validation<{
  name: 'scholarshipStatus' | 'scholarshipType' | 'borrowingPeriod' | string;
  reason: string;
}>;

export type Validators = {
  basicInfo: {
    name: NameValidator;
    preferredUsername: PreferredUsernameValidator;
    phoneNumber: PhoneNumberValidator;
    postalCode: PostalCodeValidator;
    address: AddressValidator;
    profile: ProfileValidator;
  };
  qualificationsAndWork: {
    qualificationCodeList: QualificationCodeListValidator;
    employmentId: EmploymentIdValidator;
    affiliationIds: AffiliationIdsValidator;
    activityAreaIds: ActivityAreaIdsValidator;
    licenseIds: LicenseIdsValidator;
    customLicense: CustomLicenseValidator;
    nurseLicenseNumber: NurseLicenseNumberValidator;
  };
  academicBackground: {
    school: SchoolValidator;
    scholarship: ScholarshipValidator;
  };
};

export type Updaters = {
  basicInfo: BasicInfoUpdaters;
  qualificationsAndWork: QualificationsAndWorkUpdaters;
  academicBackground: AcademicBackgroundUpdaters;
};

// 現在選択しているタブ 0: 基本情報, 1: 資格・仕事, 2: 学歴
// eslint-disable-next-line no-magic-numbers
type TabIndex = 0 | 1 | 2;

export type InitBasicInfo = {
  givenName?: string;
  familyName?: string;
  givenNameKana?: string;
  familyNameKana?: string;
  preferredUsername?: string;
  gender?: GenderId;
  phoneNumber?: string;
  prefectureId?: PrefectureId;
  postalCode?: string;
  locality?: string;
  streetAddress?: string;
  building?: string;
  birthdate?: Birthdate;
  marriageStatus?: MarriageStatusId;
  hasChildren?: HasChildrenId;
  selfIntroduction?: string;
};

export type InitQualificationsAndWork = {
  qualificationCodeList?: number[];
  employmentId?: number;
  affiliationIds?: number[];
  activityAreaIds?: number[];
  licenseIds?: number[];
  customLicense?: string;
  nurseLicenseNumber?: string;
};

export type InitAcademicBackground = {
  schoolName?: string;
  schoolTypeId?: number;
  graduationYear?: number;
  scholarshipStatus?: ScholarshipStatusId;
  scholarshipType?: ScholarshipTypeId;
  borrowingPeriod?: BorrowingPeriodId;
};

type Params = {
  validators: Validators;
  updaters: Updaters;
  loggedInUser: LoggedInUser;
  initBasicInfo?: InitBasicInfo;
  initQualificationsAndWork?: InitQualificationsAndWork;
  initAcademicBackground?: InitAcademicBackground;
  fetchAddressByPostalCode?: FetchAddressByPostalCode;
};

const defaultTabIndex = 0;

const birthYearList = createInitBirthYearList();

const birthMonthList = createInitBirthMonthList();

// eslint-disable-next-line max-lines-per-function, max-statements, complexity
export const useProfileSettingForm = ({
  validators,
  updaters,
  loggedInUser,
  initBasicInfo,
  initQualificationsAndWork,
  initAcademicBackground,
  fetchAddressByPostalCode,
}: Params) => {
  // 現在選択しているタブ 0: 基本情報, 1: 資格・仕事, 2: 学歴
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [tabIndex, setTabIndex] = useState<TabIndex>(defaultTabIndex);

  const [familyName, setFamilyName] = useState<string>(
    initBasicInfo?.familyName ? initBasicInfo?.familyName : '',
  );

  const [givenName, setGivenName] = useState<string>(
    initBasicInfo?.givenName ? initBasicInfo?.givenName : '',
  );

  const [familyNameKana, setFamilyNameKana] = useState<string>(
    initBasicInfo?.familyNameKana ? initBasicInfo?.familyNameKana : '',
  );

  const [givenNameKana, setGivenNameKana] = useState<string>(
    initBasicInfo?.givenNameKana ? initBasicInfo?.givenNameKana : '',
  );

  const [preferredUsername, setPreferredUsername] = useState<string>(
    initBasicInfo?.preferredUsername ? initBasicInfo?.preferredUsername : '',
  );

  const [gender, setGender] = useState<GenderId>();

  const [phoneNumber, setPhoneNumber] = useState<string>(
    initBasicInfo?.phoneNumber ? initBasicInfo?.phoneNumber : '',
  );

  const [postalCode, setPostalCode] = useState<string>(
    initBasicInfo?.postalCode ? initBasicInfo?.postalCode : '',
  );

  const initBirthdate = initBasicInfo?.birthdate
    ? new Date(initBasicInfo?.birthdate)
    : new Date('1990-01-01');

  // eslint-disable-next-line no-magic-numbers
  const initBirthMonth = (initBirthdate.getMonth() + 1) as BirthMonth;

  const [selectedBirthYear, setSelectedBirthYear] = useState<number>(
    initBirthdate.getFullYear(),
  );

  const [selectedBirthMonth, setSelectedBirthMonth] =
    useState<BirthMonth>(initBirthMonth);

  const [birthdayList, setBirthdayList] = useState<number[]>(
    // eslint-disable-next-line no-magic-numbers
    createBirthdayList(initBirthdate.getFullYear(), initBirthMonth),
  );

  const [selectedBirthday, setSelectedBirthday] = useState<number>(
    initBirthdate.getDate(),
  );

  const [selectedPrefectureId, setSelectedPrefectureId] =
    useState<PrefectureId>(
      initBasicInfo?.prefectureId
        ? initBasicInfo?.prefectureId
        : prefectureTokyo.id,
    );

  const [locality, setLocality] = useState<string>(
    initBasicInfo?.locality ? initBasicInfo?.locality : '',
  );

  const [streetAddress, setStreetAddress] = useState<string>(
    initBasicInfo?.streetAddress ? initBasicInfo?.streetAddress : '',
  );

  const [building, setBuilding] = useState<string>(
    initBasicInfo?.building ? initBasicInfo?.building : '',
  );

  const [selectedMarriageStatusId, setSelectedMarriageStatusId] =
    useState<MarriageStatusId>(
      initBasicInfo?.marriageStatus
        ? initBasicInfo?.marriageStatus
        : defaultMarriageStatus,
    );

  const [selectedHasChildrenId, setSelectedHasChildrenId] =
    useState<HasChildrenId>(
      initBasicInfo?.hasChildren
        ? initBasicInfo?.hasChildren
        : defaultHasChildren,
    );

  const birthYearAttributes = birthYearList.map((value, index) => ({
    key: index,
    value: String(value),
    label: String(value),
  }));

  const birthMonthAttributes = birthMonthList.map((value, index) => ({
    key: index,
    value: String(value),
    label: String(value),
  }));

  const birthdayAttributes = birthdayList.map((value, index) => ({
    key: index,
    value: String(value),
    label: String(value),
  }));

  const [basicInfoInvalidParams, setBasicInfoInvalidParams] =
    useState<ProfileSettingInvalidParams>([]);

  const basicInfoInvalidErrors = createInvalidErrors(basicInfoInvalidParams);

  const [loading, setLoading] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string>('');

  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>('');

  const [base64Image, setBase64Image] = useState<string>('');

  const [imageExtension, setImageExtension] = useState<
    AcceptedTypesImageExtension | string
  >('');

  const [selfIntroduction, setSelfIntroduction] = useState<string>(
    initBasicInfo?.selfIntroduction ? initBasicInfo?.selfIntroduction : '',
  );

  const [qualificationCodeList, setQualificationCodeList] = useState<number[]>(
    initQualificationsAndWork?.qualificationCodeList
      ? initQualificationsAndWork?.qualificationCodeList
      : [],
  );

  // 4 は「クリニック」を意味する、初期値は一番人数が多いと思われるクリニックにしている
  const [selectedEmploymentId, setSelectedEmploymentId] = useState<number>(
    initQualificationsAndWork?.employmentId
      ? initQualificationsAndWork?.employmentId
      : // eslint-disable-next-line no-magic-numbers
        4,
  );

  const [affiliationIds, setAffiliationIds] = useState<number[]>(
    initQualificationsAndWork?.affiliationIds
      ? initQualificationsAndWork?.affiliationIds
      : [],
  );

  const [activityAreaIds, setActivityAreaIds] = useState<number[]>(
    initQualificationsAndWork?.activityAreaIds
      ? initQualificationsAndWork?.activityAreaIds
      : [],
  );

  const [licenseIds, setLicenseIds] = useState<number[]>(
    initQualificationsAndWork?.licenseIds
      ? initQualificationsAndWork?.licenseIds
      : [],
  );

  const [customLicense, setCustomLicense] = useState<string>(
    initQualificationsAndWork?.customLicense
      ? initQualificationsAndWork?.customLicense
      : '',
  );

  const [nurseLicenseNumber, setNurseLicenseNumber] = useState<string>(
    initQualificationsAndWork?.nurseLicenseNumber
      ? initQualificationsAndWork?.nurseLicenseNumber
      : '',
  );

  const [schoolName, setSchoolName] = useState<string>(
    initAcademicBackground?.schoolName
      ? initAcademicBackground?.schoolName
      : '',
  );

  // 5はその他を表す
  const [schoolTypeId, setSchoolTypeId] = useState<number>(
    initAcademicBackground?.schoolTypeId
      ? initAcademicBackground?.schoolTypeId
      : // eslint-disable-next-line no-magic-numbers
        5,
  );

  const [graduationYear, setGraduationYear] = useState<number>(
    initAcademicBackground?.graduationYear
      ? initAcademicBackground?.graduationYear
      : // eslint-disable-next-line no-magic-numbers
        new Date().getFullYear() + 1,
  );

  const [scholarshipStatus, setScholarshipStatus] =
    useState<ScholarshipStatusId>(
      initAcademicBackground?.scholarshipStatus
        ? initAcademicBackground?.scholarshipStatus
        : defaultScholarshipStatus.id,
    );

  const [scholarshipType, setScholarshipType] = useState<ScholarshipTypeId>(
    initAcademicBackground?.scholarshipType
      ? initAcademicBackground?.scholarshipType
      : defaultScholarshipType.id,
  );

  const [borrowingPeriod, setBorrowingPeriod] = useState<BorrowingPeriodId>(
    initAcademicBackground?.borrowingPeriod
      ? initAcademicBackground?.borrowingPeriod
      : defaultBorrowingPeriod.id,
  );

  const [academicBackgroundInvalidParams, setAcademicBackgroundInvalidParams] =
    useState<ProfileSettingInvalidParams>([]);

  const academicBackgroundInvalidErrors = createInvalidErrors(
    academicBackgroundInvalidParams,
  );

  const onChangeSchoolName: ChangeEventHandler<HTMLInputElement> = (event) => {
    setSchoolName(event.target.value);
  };

  const onChangeSchoolType: ChangeEventHandler<HTMLSelectElement> = (event) => {
    const value = Number(event.target.value);
    setSchoolTypeId(value);
  };

  const onChangeGraduationYear: ChangeEventHandler<HTMLSelectElement> = (
    event,
  ) => {
    const value = Number(event.target.value);
    setGraduationYear(value);
  };

  const onChangeScholarshipStatus: ChangeEventHandler<HTMLSelectElement> = (
    event,
  ) => {
    const value = Number(event.target.value);
    if (isScholarshipStatusId(value)) {
      setScholarshipStatus(value);
    }
  };

  const onChangeScholarshipType: ChangeEventHandler<HTMLSelectElement> = (
    event,
  ) => {
    const value = Number(event.target.value);
    if (isScholarshipTypeId(value)) {
      setScholarshipType(value);
    }
  };

  const onChangeBorrowingPeriod: ChangeEventHandler<HTMLSelectElement> = (
    event,
  ) => {
    const value = Number(event.target.value);
    if (isBorrowingPeriodId(value)) {
      setBorrowingPeriod(value);
    }
  };

  // eslint-disable-next-line max-lines-per-function, complexity
  const submitAcademicBackground = async () => {
    setAcademicBackgroundInvalidParams([]);
    try {
      setLoading(true);

      // TODO let を回避出来る書き方に直したい
      let mergedInvalidParams = [] as ProfileSettingInvalidParams;

      if (schoolName || schoolTypeId || graduationYear) {
        const schoolValidationResult =
          await validators.academicBackground.school({
            schoolName,
            schoolTypeId,
            graduationYear,
          });
        if (
          !schoolValidationResult.isValidate &&
          schoolValidationResult.invalidParams
        ) {
          mergedInvalidParams = mergedInvalidParams.concat(
            schoolValidationResult.invalidParams,
          );
        }
      }

      if (scholarshipStatus || scholarshipType || borrowingPeriod) {
        const scholarshipValidationResult =
          await validators.academicBackground.scholarship({
            scholarshipStatus,
            scholarshipType,
            borrowingPeriod,
          });

        if (
          !scholarshipValidationResult.isValidate &&
          scholarshipValidationResult.invalidParams
        ) {
          mergedInvalidParams = mergedInvalidParams.concat(
            scholarshipValidationResult.invalidParams,
          );
        }
      }

      setAcademicBackgroundInvalidParams(mergedInvalidParams);

      // eslint-disable-next-line no-magic-numbers
      if (mergedInvalidParams.length === 0) {
        const promises = [];

        if (schoolName || schoolTypeId || graduationYear) {
          promises.push(
            (async () => {
              await updaters.academicBackground.school({
                schoolName,
                schoolTypeId,
                graduationYear,
              });
            })(),
          );
        }

        if (scholarshipStatus || scholarshipType || borrowingPeriod) {
          promises.push(
            (async () => {
              await updaters.academicBackground.scholarship({
                scholarshipStatus,
                scholarshipType,
                borrowingPeriod,
              });
            })(),
          );
        }

        // eslint-disable-next-line no-magic-numbers
        if (promises.length !== 0) {
          await Promise.all(promises);
        }
      }

      setErrorMessage('');
    } catch (error) {
      setErrorMessage(createUpdateFailedMessage(error));

      if (error instanceof Error) {
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  const [
    qualificationsAndWorkInvalidParams,
    setQualificationsAndWorkInvalidParams,
  ] = useState<ProfileSettingInvalidParams>([]);

  const qualificationsAndWorkInvalidErrors = createInvalidErrors(
    qualificationsAndWorkInvalidParams,
  );

  const onChangeQualificationCodeList: ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    const checkedQualificationCode = Number(event.target.value);

    if (qualificationCodeList.includes(checkedQualificationCode)) {
      const newQualificationCodeList = qualificationCodeList.filter(
        (value) => value !== checkedQualificationCode,
      );

      setQualificationCodeList(newQualificationCodeList);

      return;
    }

    const newQualificationCodeList = qualificationCodeList.concat([
      checkedQualificationCode,
    ]);
    setQualificationCodeList(newQualificationCodeList);
  };

  const onChangeEmploymentId: ChangeEventHandler<HTMLSelectElement> = (
    event,
  ) => {
    setSelectedEmploymentId(Number(event.target.value));
  };

  const onChangeAffiliationIds: ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    const checkedAffiliationId = Number(event.target.value);

    if (affiliationIds.includes(checkedAffiliationId)) {
      const newAffiliationIds = affiliationIds.filter(
        (value) => value !== checkedAffiliationId,
      );

      setAffiliationIds(newAffiliationIds);

      return;
    }

    const newAffiliationIds = affiliationIds.concat([checkedAffiliationId]);
    setAffiliationIds(newAffiliationIds);
  };

  const onChangeActivityAreaIds: ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    const checkedActivityAreaId = Number(event.target.value);

    if (activityAreaIds.includes(checkedActivityAreaId)) {
      const newActivityAreaIds = activityAreaIds.filter(
        (value) => value !== checkedActivityAreaId,
      );

      setActivityAreaIds(newActivityAreaIds);

      return;
    }

    const newActivityAreaIds = activityAreaIds.concat([checkedActivityAreaId]);
    setActivityAreaIds(newActivityAreaIds);
  };

  const onChangeLicenseIds: ChangeEventHandler<HTMLInputElement> = (event) => {
    const checkedLicenseId = Number(event.target.value);

    if (licenseIds.includes(checkedLicenseId)) {
      const newLicenseIds = licenseIds.filter(
        (value) => value !== checkedLicenseId,
      );

      setLicenseIds(newLicenseIds);

      return;
    }

    const newLicenseIds = licenseIds.concat([checkedLicenseId]);
    setLicenseIds(newLicenseIds);
  };

  const onChangeCustomLicense: ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    const { value } = event.target;

    setCustomLicense(value);
  };

  const onChangeNurseLicenseNumber: ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    const { value } = event.target;

    setNurseLicenseNumber(value);
  };

  // eslint-disable-next-line complexity, max-statements, max-lines-per-function
  const submitQualificationsAndWork = async () => {
    setQualificationsAndWorkInvalidParams([]);

    try {
      setLoading(true);

      // TODO let を回避出来る書き方に直したい
      let mergedInvalidParams = [] as ProfileSettingInvalidParams;

      // eslint-disable-next-line no-magic-numbers
      if (qualificationCodeList.length !== 0) {
        const validationResult =
          await validators.qualificationsAndWork.qualificationCodeList({
            qualificationCodeList,
          });

        if (!validationResult.isValidate && validationResult.invalidParams) {
          mergedInvalidParams = mergedInvalidParams.concat(
            validationResult.invalidParams,
          );
        }
      }

      if (selectedEmploymentId) {
        const validationResult =
          await validators.qualificationsAndWork.employmentId({
            employmentId: selectedEmploymentId,
          });

        if (!validationResult.isValidate && validationResult.invalidParams) {
          mergedInvalidParams = mergedInvalidParams.concat(
            validationResult.invalidParams,
          );
        }
      }

      // eslint-disable-next-line no-magic-numbers
      if (affiliationIds.length !== 0) {
        const validationResult =
          await validators.qualificationsAndWork.affiliationIds({
            affiliationIds,
          });

        if (!validationResult.isValidate && validationResult.invalidParams) {
          mergedInvalidParams = mergedInvalidParams.concat(
            validationResult.invalidParams,
          );
        }
      }

      // eslint-disable-next-line no-magic-numbers
      if (activityAreaIds.length !== 0) {
        const validationResult =
          await validators.qualificationsAndWork.activityAreaIds({
            activityAreaIds,
          });

        if (!validationResult.isValidate && validationResult.invalidParams) {
          mergedInvalidParams = mergedInvalidParams.concat(
            validationResult.invalidParams,
          );
        }
      }

      // eslint-disable-next-line no-magic-numbers
      if (licenseIds.length !== 0) {
        const validationResult =
          await validators.qualificationsAndWork.licenseIds({ licenseIds });

        if (!validationResult.isValidate && validationResult.invalidParams) {
          mergedInvalidParams = mergedInvalidParams.concat(
            validationResult.invalidParams,
          );
        }
      }

      if (customLicense) {
        const validationResult =
          await validators.qualificationsAndWork.customLicense({
            customLicense,
          });

        if (!validationResult.isValidate && validationResult.invalidParams) {
          mergedInvalidParams = mergedInvalidParams.concat(
            validationResult.invalidParams,
          );
        }
      }

      if (nurseLicenseNumber) {
        const validationResult =
          await validators.qualificationsAndWork.nurseLicenseNumber({
            nurseLicenseNumber,
          });

        if (!validationResult.isValidate && validationResult.invalidParams) {
          mergedInvalidParams = mergedInvalidParams.concat(
            validationResult.invalidParams,
          );
        }
      }

      setQualificationsAndWorkInvalidParams(mergedInvalidParams);

      // eslint-disable-next-line no-magic-numbers
      if (mergedInvalidParams.length === 0) {
        const promises = [];

        // eslint-disable-next-line no-magic-numbers
        if (qualificationCodeList.length !== 0) {
          promises.push(
            (async () => {
              await updaters.qualificationsAndWork.qualificationCodeList({
                qualificationCodeList,
              });
            })(),
          );
        }

        if (selectedEmploymentId) {
          promises.push(
            (async () => {
              await updaters.qualificationsAndWork.employmentId({
                employmentId: selectedEmploymentId,
              });
            })(),
          );
        }

        // eslint-disable-next-line no-magic-numbers
        if (affiliationIds.length !== 0) {
          promises.push(
            (async () => {
              await updaters.qualificationsAndWork.affiliationIds({
                affiliationIds,
              });
            })(),
          );
        }

        // eslint-disable-next-line no-magic-numbers
        if (activityAreaIds.length !== 0) {
          promises.push(
            (async () => {
              await updaters.qualificationsAndWork.activityAreaIds({
                activityAreaIds,
              });
            })(),
          );
        }

        // eslint-disable-next-line no-magic-numbers
        if (licenseIds.length !== 0) {
          promises.push(
            (async () => {
              await updaters.qualificationsAndWork.licenseIds({
                licenseIds,
              });
            })(),
          );
        }

        if (customLicense) {
          promises.push(
            (async () => {
              await updaters.qualificationsAndWork.customLicense({
                customLicense,
              });
            })(),
          );
        }

        if (nurseLicenseNumber) {
          promises.push(
            (async () => {
              await updaters.qualificationsAndWork.nurseLicenseNumber({
                nurseLicenseNumber,
              });
            })(),
          );
        }

        // eslint-disable-next-line no-magic-numbers
        if (promises.length !== 0) {
          await Promise.all(promises);
        }
      }

      setErrorMessage('');
    } catch (error) {
      setErrorMessage(createUpdateFailedMessage(error));

      if (error instanceof Error) {
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReaderLoaded = (event: ProgressEvent<FileReader>) => {
    if (event.target === null) {
      return;
    }

    const binaryString = event.target?.result;
    if (typeof binaryString !== 'string') {
      return;
    }

    setBase64Image(window.btoa(binaryString));
    setBasicInfoInvalidParams([]);
  };

  const mightConvertImageToBase64 = (file: File) => {
    const fileType = file.type;
    const extractedImageExtension =
      mightExtractImageExtFromValidFileType(fileType);
    if (extractedImageExtension === null) {
      setBasicInfoInvalidParams([
        {
          name: 'profileImageExtension',
          reason: `プロフィール画像のアップロードに利用出来る画像拡張子は ${acceptedTypesImageExtensions.join(
            ' ',
          )} です。`,
        },
      ]);

      setImageExtension('');
      setImagePreviewUrl('');
      setBase64Image('');

      return;
    }

    if (!isAcceptableFileSize(file)) {
      setBasicInfoInvalidParams([
        {
          name: 'profileImage',
          reason: `プロフィール画像のサイズは${acceptableSizeThreshold}MB未満でお願いします。`,
        },
      ]);

      setImageExtension('');
      setImagePreviewUrl('');
      setBase64Image('');

      return;
    }

    setImageExtension(extractedImageExtension);

    const url = URL.createObjectURL(file);
    setImagePreviewUrl(url);

    const reader = new FileReader();
    reader.onload = handleReaderLoaded;
    reader.readAsBinaryString(file);
  };

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    // eslint-disable-next-line no-magic-numbers
    if (event.target.files && event.target.files.length > 0) {
      const targetIndex = 0;
      const file = event.target.files[targetIndex];

      mightConvertImageToBase64(file);
    }
  };

  const onSelectTab = (index: number) => {
    setTabIndex(index as TabIndex);
  };

  const onChangeFamilyName: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { value } = event.target;

    setFamilyName(value);
  };

  const onChangeGivenName: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { value } = event.target;

    setGivenName(value);
  };

  const onChangeFamilyNameKana: ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    const { value } = event.target;

    setFamilyNameKana(value);
  };

  const onChangeGivenNameKana: ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    const { value } = event.target;

    setGivenNameKana(value);
  };

  const onChangePreferredUsername: ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    const { value } = event.target;

    setPreferredUsername(value);
  };

  const onChangeGender: ChangeEventHandler<HTMLSelectElement> = (event) => {
    const genderId = Number(event.target.value);
    if (isGenderId(genderId)) {
      setGender(genderId);
    }
  };

  const onChangeBirthYear: ChangeEventHandler<HTMLSelectElement> = (event) => {
    setSelectedBirthYear(Number(event.target.value));
    setBirthdayList(
      createBirthdayList(Number(event.target.value), selectedBirthMonth),
    );
  };

  const onChangeBirthMonth: ChangeEventHandler<HTMLSelectElement> = (event) => {
    setSelectedBirthMonth(Number(event.target.value) as BirthMonth);
    setBirthdayList(
      createBirthdayList(
        selectedBirthYear,
        Number(event.target.value) as BirthMonth,
      ),
    );
  };

  const onChangeBirthday: ChangeEventHandler<HTMLSelectElement> = (event) => {
    setSelectedBirthday(Number(event.target.value));
  };

  const onChangePhoneNumber: ChangeEventHandler<HTMLInputElement> = (event) => {
    setPhoneNumber(event.target.value);
  };

  const onChangePostalCode: ChangeEventHandler<HTMLInputElement> = (event) => {
    setPostalCode(event.target.value);
  };

  const onChangePrefectureId: ChangeEventHandler<HTMLSelectElement> = (
    event,
  ) => {
    const selectedValue = Number(event.target.value);
    if (isPrefectureId(selectedValue)) {
      setSelectedPrefectureId(selectedValue);
    }
  };

  const onChangeLocality: ChangeEventHandler<HTMLInputElement> = (event) => {
    setLocality(event.target.value);
  };

  const onChangeStreetAddress: ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    setStreetAddress(event.target.value);
  };

  const onChangeBuilding: ChangeEventHandler<HTMLInputElement> = (event) => {
    setBuilding(event.target.value);
  };

  const handlePostalCodeFocusEvent: FocusEventHandler<
    HTMLInputElement
  > = async () => {
    if (fetchAddressByPostalCode) {
      if (postalCode) {
        const postalCodeValidationResult =
          await validators.basicInfo.postalCode({ postalCode });
        if (postalCodeValidationResult.isValidate) {
          const fetchAddressResult = await fetchAddressByPostalCode(postalCode);
          if (isSuccessResult(fetchAddressResult)) {
            setSelectedPrefectureId(fetchAddressResult.value.prefectureId);
            setLocality(fetchAddressResult.value.locality);
          }
        }
      }
    }
  };

  const onChangeMarriageStatusId: ChangeEventHandler<HTMLSelectElement> = (
    event,
  ) => {
    const selectedValue = Number(event.target.value);
    if (isMarriageStatusId(selectedValue)) {
      setSelectedMarriageStatusId(selectedValue);
    }
  };

  const onChangeHasChildrenId: ChangeEventHandler<HTMLSelectElement> = (
    event,
  ) => {
    const selectedValue = Number(event.target.value);
    if (isHasChildrenId(selectedValue)) {
      setSelectedHasChildrenId(selectedValue);
    }
  };

  const onChangeSelfIntroduction: ChangeEventHandler<HTMLTextAreaElement> = (
    event,
  ) => {
    setSelfIntroduction(event.target.value);
  };

  // eslint-disable-next-line max-lines-per-function, max-statements, complexity
  const submitBasicInfo = async () => {
    setBasicInfoInvalidParams([]);

    try {
      const updateBasicInfoColumns = createInitUpdateBasicInfoColumns();

      // TODO let を回避出来る書き方に直したい
      let mergedInvalidParams = [] as ProfileSettingInvalidParams;

      if (familyName || givenName) {
        updateBasicInfoColumns.name = true;

        const names = {
          givenName,
          familyName,
          familyNameKana,
          givenNameKana,
        };

        const namesValidationResult = await validators.basicInfo.name(names);
        if (
          !namesValidationResult.isValidate &&
          namesValidationResult.invalidParams
        ) {
          mergedInvalidParams = mergedInvalidParams.concat(
            namesValidationResult.invalidParams,
          );
        }
      }

      if (preferredUsername) {
        updateBasicInfoColumns.preferredUsername = true;

        const preferredUsernameValidationError =
          await validators.basicInfo.preferredUsername({ preferredUsername });
        if (
          !preferredUsernameValidationError.isValidate &&
          preferredUsernameValidationError.invalidParams
        ) {
          mergedInvalidParams = mergedInvalidParams.concat(
            preferredUsernameValidationError.invalidParams,
          );
        }
      }

      if (gender) {
        updateBasicInfoColumns.gender = true;

        const genderValidationResult = await genderValidator(gender);
        if (
          !genderValidationResult.isValidate &&
          genderValidationResult.invalidParams
        ) {
          mergedInvalidParams = mergedInvalidParams.concat(
            genderValidationResult.invalidParams,
          );
        }
      }

      if (phoneNumber) {
        updateBasicInfoColumns.phoneNumber = true;

        const phoneNumberValidationResult =
          await validators.basicInfo.phoneNumber({
            phoneNumber,
          });
        if (
          !phoneNumberValidationResult.isValidate &&
          phoneNumberValidationResult.invalidParams
        ) {
          mergedInvalidParams = mergedInvalidParams.concat(
            phoneNumberValidationResult.invalidParams,
          );
        }
      }

      if (
        isAddressUpdate({
          postalCode,
          prefectureId: selectedPrefectureId,
          locality,
          streetAddress,
        })
      ) {
        updateBasicInfoColumns.address = true;

        const addressUpdateRequest = {
          postalCode,
          prefectureId: selectedPrefectureId,
          locality,
          streetAddress,
          building,
        };

        const addressValidationResult =
          await validators.basicInfo.address(addressUpdateRequest);
        if (
          !addressValidationResult.isValidate &&
          addressValidationResult.invalidParams
        ) {
          mergedInvalidParams = mergedInvalidParams.concat(
            addressValidationResult.invalidParams,
          );
        }
      }

      const birthdate = createBirthdate({
        birthYear: selectedBirthYear,
        birthMonth: selectedBirthMonth,
        birthday: selectedBirthday,
      });

      // eslint-disable-next-line no-eq-null
      if (selectedMarriageStatusId || selectedHasChildrenId != null) {
        updateBasicInfoColumns.familyStructure = true;

        const familyStructureValidationResult = familyStructureValidator({
          marriageStatus: selectedMarriageStatusId,
          hasChildren: selectedHasChildrenId,
        });
        if (
          !familyStructureValidationResult.isValidate &&
          familyStructureValidationResult.invalidParams
        ) {
          mergedInvalidParams = mergedInvalidParams.concat(
            familyStructureValidationResult.invalidParams,
          );
        }
      }

      if ((base64Image && imageExtension) || selfIntroduction) {
        updateBasicInfoColumns.profile = true;
        const profileValidationRequest = createProfileUpdaterRequest({
          profileImage: base64Image,
          profileImageExtension: imageExtension,
          selfIntroduction,
        });

        const profileValidationResult = await validators.basicInfo.profile(
          profileValidationRequest,
        );
        if (
          !profileValidationResult.isValidate &&
          profileValidationResult.invalidParams
        ) {
          mergedInvalidParams = mergedInvalidParams.concat(
            profileValidationResult.invalidParams,
          );
        }
      }

      setBasicInfoInvalidParams(mergedInvalidParams);

      // eslint-disable-next-line no-magic-numbers
      if (mergedInvalidParams.length === 0) {
        const promises = [
          (async () => {
            await updaters.basicInfo.birthdate({
              birthdate,
            });
          })(),
        ];

        if (
          updateBasicInfoColumns.name &&
          givenName &&
          familyName &&
          givenNameKana &&
          familyNameKana
        ) {
          promises.push(
            (async () => {
              await updaters.basicInfo.name({
                givenName,
                familyName,
                givenNameKana,
                familyNameKana,
              });
            })(),
          );
        }

        if (updateBasicInfoColumns.preferredUsername && preferredUsername) {
          promises.push(
            (async () => {
              await updaters.basicInfo.preferredUsername({
                preferredUsername,
              });
            })(),
          );
        }

        if (updateBasicInfoColumns.gender && gender) {
          promises.push(
            (async () => {
              await updaters.basicInfo.gender({
                gender,
              });
            })(),
          );
        }

        if (updateBasicInfoColumns.phoneNumber && phoneNumber) {
          promises.push(
            (async () => {
              await updaters.basicInfo.phoneNumber({
                phoneNumber,
              });
            })(),
          );
        }

        if (
          updateBasicInfoColumns.address &&
          postalCode &&
          selectedPrefectureId &&
          locality &&
          streetAddress
        ) {
          const updateAddressRequest = building
            ? {
                sub: loggedInUser.sub,
                postalCode,
                prefectureId: selectedPrefectureId,
                locality,
                streetAddress,
                building,
              }
            : {
                sub: loggedInUser.sub,
                postalCode,
                prefectureId: selectedPrefectureId,
                locality,
                streetAddress,
              };

          promises.push(
            (async () => {
              await updaters.basicInfo.address(updateAddressRequest);
            })(),
          );
        }

        if (
          updateBasicInfoColumns.familyStructure &&
          selectedMarriageStatusId &&
          // eslint-disable-next-line no-eq-null
          selectedHasChildrenId != null
        ) {
          promises.push(
            (async () => {
              await updaters.basicInfo.familyStructure({
                marriageStatus: selectedMarriageStatusId,
                hasChildren: selectedHasChildrenId,
              });
            })(),
          );
        }

        if ((base64Image && imageExtension) || selfIntroduction) {
          const updateProfileRequest = createProfileUpdaterRequest({
            profileImage: base64Image,
            profileImageExtension: imageExtension,
            selfIntroduction,
          });

          promises.push(
            (async () => {
              await updaters.basicInfo.profile(updateProfileRequest);
            })(),
          );
        }

        setLoading(true);

        await Promise.all(promises);

        setErrorMessage('');
      }
    } catch (error) {
      setErrorMessage(createUpdateFailedMessage(error));

      if (error instanceof Error) {
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  const submit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();

    // 基本情報の変更
    // eslint-disable-next-line no-magic-numbers
    if (tabIndex === 0) {
      await submitBasicInfo();
    }

    // 資格・仕事の変更
    // eslint-disable-next-line no-magic-numbers
    if (tabIndex === 1) {
      await submitQualificationsAndWork();
    }

    // 学歴
    // eslint-disable-next-line no-magic-numbers
    if (tabIndex === 2) {
      await submitAcademicBackground();
    }
  };

  const nameErrorMessage = `${
    basicInfoInvalidErrors?.familyNameError?.reason
      ? basicInfoInvalidErrors?.familyNameError?.reason
      : ''
  }${
    basicInfoInvalidErrors?.givenNameError?.reason
      ? basicInfoInvalidErrors?.givenNameError?.reason
      : ''
  }`;

  const kanaErrorMessage = `${
    basicInfoInvalidErrors?.familyNameKanaError?.reason
      ? basicInfoInvalidErrors?.familyNameKanaError?.reason
      : ''
  }${
    basicInfoInvalidErrors?.givenNameKanaError?.reason
      ? basicInfoInvalidErrors?.givenNameKanaError?.reason
      : ''
  }`;

  const profileImageMessage = `${
    basicInfoInvalidErrors?.profileImageError?.reason
      ? basicInfoInvalidErrors?.profileImageError?.reason
      : ''
  }${
    basicInfoInvalidErrors?.profileImageExtensionError?.reason
      ? basicInfoInvalidErrors?.profileImageExtensionError?.reason
      : ''
  }`;

  return {
    tabIndex,
    familyName,
    givenName,
    familyNameKana,
    givenNameKana,
    preferredUsername,
    gender,
    phoneNumber,
    postalCode,
    initBirthdate,
    initBirthMonth,
    selectedBirthYear,
    selectedBirthMonth,
    birthdayList,
    selectedBirthday,
    selectedPrefectureId,
    locality,
    streetAddress,
    building,
    selectedMarriageStatusId,
    selectedHasChildrenId,
    birthYearAttributes,
    birthMonthAttributes,
    birthdayAttributes,
    basicInfoInvalidParams,
    basicInfoInvalidErrors,
    qualificationsAndWorkInvalidErrors,
    loading,
    errorMessage,
    imagePreviewUrl,
    selfIntroduction,
    qualificationCodeList,
    selectedEmploymentId,
    affiliationIds,
    activityAreaIds,
    licenseIds,
    customLicense,
    nurseLicenseNumber,
    schoolName,
    schoolTypeId,
    graduationYear,
    scholarshipStatus,
    scholarshipType,
    borrowingPeriod,
    academicBackgroundInvalidErrors,
    handleFileUpload,
    onSelectTab,
    onChangeFamilyName,
    onChangeGivenName,
    onChangeFamilyNameKana,
    onChangeGivenNameKana,
    onChangePreferredUsername,
    onChangeGender,
    onChangeBirthYear,
    onChangeBirthMonth,
    onChangeBirthday,
    onChangePhoneNumber,
    onChangePostalCode,
    onChangePrefectureId,
    onChangeLocality,
    onChangeStreetAddress,
    onChangeBuilding,
    handlePostalCodeFocusEvent,
    onChangeMarriageStatusId,
    onChangeHasChildrenId,
    onChangeSelfIntroduction,
    onChangeQualificationCodeList,
    onChangeEmploymentId,
    onChangeAffiliationIds,
    onChangeActivityAreaIds,
    onChangeLicenseIds,
    onChangeCustomLicense,
    onChangeNurseLicenseNumber,
    onChangeSchoolName,
    onChangeSchoolType,
    onChangeGraduationYear,
    onChangeScholarshipStatus,
    onChangeScholarshipType,
    onChangeBorrowingPeriod,
    submit,
    nameErrorMessage,
    kanaErrorMessage,
    profileImageMessage,
  };
};
